import { HttpErrorResponse, HttpClient } from '@angular/common/http';
import { selfHandledAuthContext, skipGlobalErrorContext } from '../../shared/services/guards/http-error.context';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  finalize,
  firstValueFrom,
  map,
  Observable,
  of,
  retry,
  shareReplay,
  tap,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import { PersistenceService } from '../../shared/services/persistence.service';
import {
  CrmSendEmailReq,
  CrmSendEmailRes,
  CrmSendSmsReq,
  CrmSendSmsRes,
  EmailLoginReq,
  LinkEmailConfirmReq,
  LinkEmailConfirmRes,
  LinkEmailSendCodeReq,
  LinkEmailSendCodeRes,
  LoginReq,
  LoginRes,
} from '../interfaces/auth.interface';
import { environment } from '../../../../environments/environment';
import { SystemUserService } from '../../shared/services/system-user.service';
import { StoredUser } from '../../../interfaces/app.interface';
import { AuthStore } from './auth.store';
import { AuthStepEnum } from '../enums/auth.enum';
import { isLongLivedAccessToken } from '../helpers/access-token-lifetime.helper';
import {
  authLog,
  createAuthTraceId,
  describeHttpError,
} from '../helpers/auth-diagnostics.util';
import { RefreshLockService } from './refresh-lock.service';
import { AuthTransportService, COOKIE_RT_SENTINEL } from './auth-transport.service';

// Потолок времени на один /auth/refresh. Под межвкладочной блокировкой зависший
// запрос держал бы блокировку и морозил рефреш во всех вкладках — таймаут её отпускает.
const REFRESH_REQUEST_TIMEOUT_MS = 20000;

// Быстрый транзиентный сбой (обрыв сети — status 0, редеплой API — 5xx, серверный
// 408) ретраим сразу, чтобы не ронять исходный запрос и не заставлять юзера тыкать
// повторно. Клиентский таймаут (rxjs TimeoutError, 20 c) НЕ ретраим: запрос мог
// дойти и провернуть токен на сервере — повтор держал бы межвкладочную блокировку;
// такой lost-response восстановит следующий рефреш в пределах серверного grace-окна.
const REFRESH_MAX_RETRIES = 2;
const REFRESH_RETRY_BACKOFF_MS = 800;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public currentUser$ = new BehaviorSubject<StoredUser>(null!);
  public access_token$ = new BehaviorSubject<string>('');
  private readonly refreshSupportedHeaders = {
    Anonymous: 'true',
    'X-Auth-Refresh-Supported': 'true',
    'X-Auth-Client': 'crm',
  };
  private refreshTokenRequest$: Observable<string> | null = null;
  private longLivedAccessTokenRefreshPromise: Promise<void> | null = null;
  private sessionGeneration = 0;

  constructor(
    private systemUserService: SystemUserService,
    private http: HttpClient,
    private persistenceService: PersistenceService,
    private authStore: AuthStore,
    private refreshLock: RefreshLockService,
    private authTransport: AuthTransportService
  ) {}

  /** База auth-эндпоинтов: в cookie-режиме — same-site API-хост (кука first-party). */
  private get authApiUrl(): string {
    return this.authTransport.get().apiUrl;
  }

  /** true → refresh-токен в httpOnly-куке, а не в теле/localStorage (веб-CRM на проде). */
  private get useCookieTransport(): boolean {
    return this.authTransport.get().mode === 'cookie';
  }

  /** withCredentials нужен, чтобы браузер слал/принимал httpOnly-куку на auth-запросах. */
  private get withCredentials(): boolean {
    return this.authTransport.get().withCredentials;
  }

  public async initAuthServiceVariables(): Promise<void> {
    const storedUser = this.systemUserService.getCurrentUser();
    const accessToken = this.systemUserService.getAccessToken();
    const refreshToken = this.systemUserService.getRefreshToken();

    authLog('info', 'crm.auth.init.start', {
      hasStoredUser: !!storedUser,
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
    });

    if (!storedUser || !accessToken) {
      this.persistenceService.remove('user');
      this.persistenceService.remove('accessToken');
      this.persistenceService.remove('refreshToken');
      this.resetSessionState();
      authLog('warn', 'crm.auth.init.cleared_missing_session', {
        hadStoredUser: !!storedUser,
        hadAccessToken: !!accessToken,
        hadRefreshToken: !!refreshToken,
      });
      return;
    }

    this.setSessionState({
      ...storedUser,
      access_token: accessToken,
    });
    void this.refreshLongLivedAccessToken(accessToken);
    authLog('info', 'crm.auth.init.restored', {
      ...this.describeStoredUser(storedUser),
      hasAccessToken: true,
      hasRefreshToken: !!refreshToken,
    });
  }

  public resetSessionState(): void {
    this.sessionGeneration += 1;
    this.currentUser$.next(null!);
    this.access_token$.next('');
    this.authStore.authStep$.next(AuthStepEnum.LOGIN);
    this.authStore.phone$.next('');
    this.authStore.email$.next('');
    this.authStore.channel$.next('phone');
    this.authStore.isTestAccount$.next(false);
    authLog('info', 'crm.auth.session.reset', {
      sessionGeneration: this.sessionGeneration,
    });
  }

  public updateCurrentUser(storedUser: StoredUser): void {
    const { refresh_token: refreshToken, ...userWithoutRefreshToken } = storedUser;

    this.persistenceService.set('accessToken', storedUser.access_token);
    if (this.useCookieTransport) {
      // Реальный RT — в httpOnly-куке (в ответе его нет). В localStorage кладём маркер,
      // чтобы существующие проверки «есть ли сессия» работали, и чтобы затереть
      // возможный легаси-RT (одноразовая миграция со старого localStorage-хранения).
      this.persistenceService.set('refreshToken', COOKIE_RT_SENTINEL);
    } else if (refreshToken) {
      this.persistenceService.set('refreshToken', refreshToken);
    }
    this.persistenceService.set('user', userWithoutRefreshToken);
    this.setSessionState(userWithoutRefreshToken);
    authLog('info', 'crm.auth.user.persisted', {
      ...this.describeStoredUser(storedUser),
      hasAccessToken: !!storedUser.access_token,
      // В cookie-режиме реального RT в теле нет (он в куке), но сессия с RT есть —
      // логируем true, чтобы RCA-маркеры не противоречили init/logout, читающим sentinel.
      transport: this.useCookieTransport ? 'cookie' : 'body',
      hasRefreshToken: this.useCookieTransport || !!refreshToken,
      refreshTokenExpiresAt: storedUser.refresh_token_expires_at ?? null,
    });
  }

  public crmSendSms(data: CrmSendSmsReq): Observable<CrmSendSmsRes> {
    const traceId = createAuthTraceId('crm-send-sms');
    authLog('info', 'crm.sms.send.start', {
      traceId,
      phoneLength: data?.phone?.length ?? null,
    });

    return this.http.post<CrmSendSmsRes>(`${this.authApiUrl}/auth/crm-send-sms`, data, {
      headers: this.authHeaders(traceId),
      withCredentials: this.withCredentials,
    }).pipe(
      tap({
        next: (response) => {
          authLog('info', 'crm.sms.send.success', {
            traceId,
            smsSent: response?.smsSent ?? null,
            testAccount: response?.testAccount ?? null,
          });
        },
        error: (error) => {
          authLog('warn', 'crm.sms.send.error', {
            traceId,
            error: describeHttpError(error),
          });
        },
      })
    );
  }

  public login(data: LoginReq): Observable<LoginRes> {
    const traceId = createAuthTraceId('crm-login');
    authLog('info', 'crm.login.start', {
      traceId,
      phoneLength: data?.phone?.length ?? null,
      smsCodeLength: data?.smsCode?.length ?? null,
    });

    return this.http
      .post<LoginRes>(`${this.authApiUrl}/auth/login`, data, {
        headers: this.authHeaders(traceId),
        withCredentials: this.withCredentials,
      })
      .pipe(
        tap({
          next: ({ access_token, ...data }) => {
          this.updateCurrentUser({
            access_token,
            ...data,
          });
            authLog('info', 'crm.login.success', {
              traceId,
              ...this.describeStoredUser(data),
              refreshTokenExpiresAt: data.refresh_token_expires_at ?? null,
            });
          },
          error: (error) => {
            authLog('warn', 'crm.login.error', {
              traceId,
              error: describeHttpError(error),
            });
          },
        })
      );
  }

  // Зеркалит crmSendSms(): Anonymous-заголовок мимо TokenInterceptor + cookie-транспорт
  // (authApiUrl/withCredentials/authHeaders), чтобы email-код слал куку так же, как SMS.
  public crmSendEmailCode(data: CrmSendEmailReq): Observable<CrmSendEmailRes> {
    const traceId = createAuthTraceId('crm-send-email');
    return this.http.post<CrmSendEmailRes>(
      `${this.authApiUrl}/auth/crm-email-send-code`,
      data,
      {
        headers: this.authHeaders(traceId),
        withCredentials: this.withCredentials,
      }
    );
  }

  // Зеркалит login(): та же cookie-aware персистентность через updateCurrentUser().
  // withCredentials обязателен — иначе браузер не примет httpOnly-RT из /auth/email-auth
  // и email-сессия не переживёт первый refresh.
  public loginByEmail(data: EmailLoginReq): Observable<LoginRes> {
    const traceId = createAuthTraceId('crm-login-email');
    return this.http
      .post<LoginRes>(`${this.authApiUrl}/auth/email-auth`, data, {
        headers: this.authHeaders(traceId),
        withCredentials: this.withCredentials,
      })
      .pipe(
        tap(({ access_token, ...data }) => {
          this.updateCurrentUser({
            access_token,
            ...data,
          });
        })
      );
  }

  // Привязка email как метода входа. В ОТЛИЧИЕ от crmSendEmailCode/loginByEmail
  // (pre-login, Anonymous-заголовок) — это АУТЕНТИФИЦИРОВАННЫЕ запросы: бьём в
  // environment.apiUrl без authHeaders/Anonymous/withCredentials, чтобы
  // TokenInterceptor подставил Authorization: Bearer <token> (как UsersService.getMe).
  // skipGlobalErrorContext(): без него TokenInterceptor на ошибке показал бы generic
  // «Произошла ошибка» И ПРОГЛОТИЛ бы её (return of()), из-за чего catchError диалога
  // не сработал бы и конкретное сообщение (409 «уже используется» / 429 cooldown /
  // 401 «неверный код») не показалось бы. С контекстом ошибка прокидывается в диалог.
  public linkEmailSendCode(data: LinkEmailSendCodeReq): Observable<LinkEmailSendCodeRes> {
    return this.http.post<LinkEmailSendCodeRes>(
      `${environment.apiUrl}/auth/link-email/send-code`,
      data,
      { context: skipGlobalErrorContext() }
    );
  }

  public linkEmailConfirm(data: LinkEmailConfirmReq): Observable<LinkEmailConfirmRes> {
    // selfHandledAuthContext (не skipGlobalErrorContext): у confirm 401 = «Неверный код»
    // (бизнес-ошибка), НЕ истёкший токен — интерцептор не должен на неё ротировать RT и
    // повторять неверный код (двойной расход локаута). Диалог сам покажет ошибку.
    return this.http.post<LinkEmailConfirmRes>(
      `${environment.apiUrl}/auth/link-email/confirm`,
      data,
      { context: selfHandledAuthContext() }
    );
  }

  public refreshAccessToken(traceId = createAuthTraceId('crm-refresh')): Observable<string> {
    if (this.refreshTokenRequest$) {
      authLog('info', 'crm.refresh.deduped', {
        traceId,
        sessionGeneration: this.sessionGeneration,
      });
      return this.refreshTokenRequest$;
    }

    // Снимок до входа в межвкладочную блокировку — по нему поймём, не обновила ли
    // сессию соседняя вкладка, пока мы ждали своей очереди.
    // In-memory значение принадлежит именно ЭТОЙ вкладке. localStorage общий:
    // соседняя вкладка могла закончить refresh ещё до того, как мы запросили lock.
    // Если снять оба значения из localStorage, такая уже завершившаяся ротация
    // останется незамеченной и мы зря провернём RT второй раз.
    const accessTokenBeforeLock =
      this.access_token$.value || this.systemUserService.getAccessToken();
    const generationBeforeLock = this.sessionGeneration;

    this.refreshTokenRequest$ = this.refreshLock
      .runExclusive(() =>
        this.refreshUnderCrossTabLock(traceId, accessTokenBeforeLock, generationBeforeLock)
      )
      .pipe(
        finalize(() => {
          authLog('info', 'crm.refresh.finalize', {
            traceId,
            sessionGeneration: this.sessionGeneration,
          });
          this.refreshTokenRequest$ = null;
        }),
        shareReplay(1)
      );

    return this.refreshTokenRequest$;
  }

  private refreshUnderCrossTabLock(
    traceId: string,
    accessTokenBeforeLock: string,
    generationBeforeLock: number
  ): Observable<string> {
    // Пока ждали блокировку, в ЭТОЙ вкладке мог случиться logout/reset — не воскрешаем.
    if (this.sessionGeneration !== generationBeforeLock) {
      authLog('warn', 'crm.refresh.session_reset_before_lock', {
        traceId,
        requestSessionGeneration: generationBeforeLock,
        currentSessionGeneration: this.sessionGeneration,
      });
      return throwError(() => new Error('Refresh session was cleared'));
    }

    // Соседняя вкладка могла уже провернуть рефреш, пока мы ждали блокировку. Тогда в
    // общем localStorage лежит свежий access-токен — подхватываем его и НЕ шлём ещё
    // один /auth/refresh уже устаревшим refresh-токеном (иначе бэкенд засчитает
    // переиспользование одноразового токена и отзовёт ВСЕ сессии пользователя).
    const currentAccessToken = this.systemUserService.getAccessToken();
    if (
      currentAccessToken &&
      currentAccessToken !== accessTokenBeforeLock &&
      this.systemUserService.getRefreshToken()
    ) {
      authLog('info', 'crm.refresh.adopted_sibling_tab', {
        traceId,
        sessionGeneration: this.sessionGeneration,
      });
      this.adoptStoredSession(currentAccessToken);
      return of(currentAccessToken);
    }

    return this.performTokenRefresh(traceId);
  }

  private performTokenRefresh(traceId: string): Observable<string> {
    const storedRefreshToken = this.systemUserService.getRefreshToken();

    // В body-режиме RT обязателен (мобилка/дев). В cookie-режиме реальный RT — в
    // httpOnly-куке, JS его не видит: не требуем и не отправляем его, полагаемся на
    // куку. В теле шлём лишь легаси-токен из localStorage — одноразовая миграция
    // уже залогиненной сессии на куку (после первого успешного рефреша маркер
    // затрёт легаси-токен, см. updateCurrentUser).
    if (!this.useCookieTransport && !storedRefreshToken) {
      authLog('warn', 'crm.refresh.missing_token', {
        traceId,
        sessionGeneration: this.sessionGeneration,
      });
      return throwError(() => new Error('Missing refresh token'));
    }

    const migrationRefreshToken =
      this.useCookieTransport &&
      storedRefreshToken &&
      storedRefreshToken !== COOKIE_RT_SENTINEL
        ? storedRefreshToken
        : null;

    // Cookie-режим: если сессии нет вовсе (после logout/reset — ни access-токена, ни
    // user, ни легаси-токена для миграции), сетевой рефреш бессмыслен. Короткозамыкаем,
    // чтобы не слать пустой /auth/refresh (in-flight запрос, стартовавший до logout,
    // иначе даёт ложную пару crm.refresh.start/401 в логах).
    if (
      this.useCookieTransport &&
      !migrationRefreshToken &&
      !this.systemUserService.getAccessToken() &&
      !this.systemUserService.getCurrentUser()
    ) {
      authLog('warn', 'crm.refresh.missing_token', {
        traceId,
        sessionGeneration: this.sessionGeneration,
      });
      return throwError(() => new Error('Missing refresh token'));
    }
    const requestBody = this.useCookieTransport
      ? migrationRefreshToken
        ? { refresh_token: migrationRefreshToken }
        : {}
      : { refresh_token: storedRefreshToken };

    const refreshSessionGeneration = this.sessionGeneration;
    authLog('info', 'crm.refresh.start', {
      traceId,
      sessionGeneration: refreshSessionGeneration,
    });

    return this.http
      .post<LoginRes>(`${this.authApiUrl}/auth/refresh`, requestBody, {
        headers: this.authHeaders(traceId),
        withCredentials: this.withCredentials,
      })
      .pipe(
        timeout({ each: REFRESH_REQUEST_TIMEOUT_MS }),
        tap({
          next: (storedUser) => {
            const currentRefreshToken = this.systemUserService.getRefreshToken();

            // Гасим успешный ответ рефреша только при настоящем сбросе сессии в ЭТОЙ
            // вкладке: сменилось поколение (logout/reset) ИЛИ refresh-токен пропал.
            // Раньше тут был ещё `getRefreshToken() !== refreshToken`, срабатывавший на
            // штатную ротацию токена соседней вкладкой и разлогинивавший живого юзера.
            if (this.sessionGeneration !== refreshSessionGeneration || !currentRefreshToken) {
              authLog('warn', 'crm.refresh.stale_response', {
                traceId,
                requestSessionGeneration: refreshSessionGeneration,
                currentSessionGeneration: this.sessionGeneration,
              });
              throw new Error('Refresh session was cleared');
            }

            this.updateCurrentUser(storedUser);
            authLog('info', 'crm.refresh.success', {
              traceId,
              ...this.describeStoredUser(storedUser),
              refreshTokenExpiresAt: storedUser.refresh_token_expires_at ?? null,
            });
          },
          error: (error) => {
            authLog('warn', 'crm.refresh.error', {
              traceId,
              sessionGeneration: refreshSessionGeneration,
              error: describeHttpError(error),
            });
          },
        }),
        retry({
          count: REFRESH_MAX_RETRIES,
          delay: (error, retryCount) => {
            if (!this.isRetriableRefreshError(error)) {
              // Определённый отказ (401/403 от бэка, «Refresh session was cleared»,
              // «Missing refresh token», клиентский TimeoutError) — не ретраим.
              return throwError(() => error);
            }
            authLog('info', 'crm.refresh.retry', {
              traceId,
              retryCount,
              error: describeHttpError(error),
            });
            return timer(retryCount * REFRESH_RETRY_BACKOFF_MS);
          },
        }),
        map((storedUser) => storedUser.access_token)
      );
  }

  /**
   * Только быстрые транзиентные сбои сети/сервера. Клиентский таймаут (rxjs
   * TimeoutError) и не-HTTP ошибки (сброс сессии, отсутствие токена) сюда НЕ
   * попадают — их ретраить нельзя (см. комментарии к константам ретрая).
   */
  private isRetriableRefreshError(error: unknown): boolean {
    if (error instanceof HttpErrorResponse) {
      return error.status === 0 || error.status === 408 || error.status >= 500;
    }
    return false;
  }

  private adoptStoredSession(accessToken: string): void {
    const storedUser = this.systemUserService.getCurrentUser();

    if (storedUser) {
      this.setSessionState({ ...storedUser, access_token: accessToken });
    } else {
      this.access_token$.next(accessToken);
    }
  }

  public revokeRefreshToken(refreshToken: string): Observable<unknown> {
    const traceId = createAuthTraceId('crm-logout');
    authLog('info', 'crm.refresh.revoke.start', {
      traceId,
      hasRefreshToken: !!refreshToken,
    });

    // В cookie-режиме сервер отзывает сессию по RT из куки; маркер в теле не шлём.
    // Легаси-токен (если ещё не мигрировал) шлём в теле, чтобы отозвать и его.
    const requestBody =
      this.useCookieTransport
        ? refreshToken && refreshToken !== COOKIE_RT_SENTINEL
          ? { refresh_token: refreshToken }
          : {}
        : { refresh_token: refreshToken };

    return this.http.post(`${this.authApiUrl}/auth/logout`, requestBody, {
      headers: this.authHeaders(traceId),
      withCredentials: this.withCredentials,
    }).pipe(
      tap({
        next: () => {
          authLog('info', 'crm.refresh.revoke.success', {
            traceId,
          });
        },
        error: (error) => {
          authLog('warn', 'crm.refresh.revoke.error', {
            traceId,
            error: describeHttpError(error),
          });
        },
      })
    );
  }

  private setSessionState(storedUser: StoredUser): void {
    this.currentUser$.next(storedUser);
    this.access_token$.next(storedUser.access_token);
  }

  private authHeaders(traceId?: string): Record<string, string> {
    const headers: Record<string, string> = { ...this.refreshSupportedHeaders };

    if (this.useCookieTransport) {
      headers['X-Auth-Refresh-Transport'] = 'cookie';
    }
    if (traceId) {
      headers['X-Auth-Debug-Id'] = traceId;
    }

    return headers;
  }

  private describeStoredUser(storedUser?: Partial<StoredUser> | null): Record<string, unknown> {
    const record = storedUser as (Partial<StoredUser> & { _id?: string; tenantId?: string }) | null | undefined;

    return {
      userId: record?._id ?? null,
      role: record?.role ?? null,
      tenantId: record?.tenantId ?? null,
    };
  }

  private async refreshLongLivedAccessToken(accessToken: string): Promise<void> {
    if (!isLongLivedAccessToken(accessToken) || this.longLivedAccessTokenRefreshPromise) {
      if (this.longLivedAccessTokenRefreshPromise) {
        authLog('info', 'crm.proactive_refresh.deduped', {
          sessionGeneration: this.sessionGeneration,
        });
      }
      return;
    }

    if (!this.systemUserService.getRefreshToken()) {
      authLog('warn', 'crm.proactive_refresh.missing_refresh_token', {
        sessionGeneration: this.sessionGeneration,
      });
      return;
    }

    const traceId = createAuthTraceId('crm-proactive-refresh');
    authLog('info', 'crm.proactive_refresh.start', {
      traceId,
      sessionGeneration: this.sessionGeneration,
    });

    this.longLivedAccessTokenRefreshPromise = firstValueFrom(this.refreshAccessToken(traceId))
      .then(() => {
        authLog('info', 'crm.proactive_refresh.success', {
          traceId,
          sessionGeneration: this.sessionGeneration,
        });
      })
      .catch((error) => {
        authLog('warn', 'crm.proactive_refresh.error', {
          traceId,
          error: describeHttpError(error),
        });
      })
      .finally(() => {
        authLog('info', 'crm.proactive_refresh.finalize', {
          traceId,
          sessionGeneration: this.sessionGeneration,
        });
        this.longLivedAccessTokenRefreshPromise = null;
      });

    await this.longLivedAccessTokenRefreshPromise;
  }
}
